<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { isAcceptable } from './view.js'
import Docviewer from './modal.vue'

const props = defineProps({
  upload: Object,
  inModal: {
    type: Boolean,
    default: true,
  },
  isLink: {
    type: Boolean,
    default: false,
  },
  versioning: Boolean,
  /** false — не трогать history (иначе back закрывает /tickets/new) */
  useHistory: {
    type: Boolean,
    default: true,
  },
  /** Прямая ссылка на просмотр (blob:/URL) */
  link: {
    type: String,
    default: null,
  },
  /** URL для скачивания с авторизацией */
  downloadUrl: {
    type: String,
    default: null,
  },
  /**
   * Опционально: async () => ({ link, upload?, downloadUrl? }) перед открытием.
   * Нужен для документов вне Uploads (например планы ЕППЛИ).
   */
  resolveSource: {
    type: Function,
    default: null,
  },
  /** async (versionItem) => ({ link, downloadUrl?, upload? }) */
  resolveVersion: {
    type: Function,
    default: null,
  },
})
const docViewerVisible = ref(false)
const resolving = ref(false)
const resolvedLink = ref(null)
const resolvedUpload = ref(null)
const resolvedDownloadUrl = ref(null)
const fileOk = computed(() => !!props.link || !!props.resolveSource || isAcceptable(props.upload))
const router = useRouter()

const activeUpload = computed(() => resolvedUpload.value || props.upload)
const activeLink = computed(() => resolvedLink.value || props.link)
const activeDownloadUrl = computed(() => resolvedDownloadUrl.value || props.downloadUrl)

async function openDoc() {
  if (!props.inModal) {
    router.push({ name: 'docview', params: { filename: props.upload.secure_filename } })
    return
  }
  if (props.resolveSource) {
    resolving.value = true
    try {
      const src = await props.resolveSource()
      if (!src?.link && !src?.upload) return
      resolvedLink.value = src.link ?? null
      resolvedUpload.value = src.upload ?? props.upload
      resolvedDownloadUrl.value = src.downloadUrl ?? props.downloadUrl
    } catch (e) {
      alert(e?.message || 'Не удалось открыть документ')
      return
    } finally {
      resolving.value = false
    }
  }
  docViewerVisible.value = true
}

function onHidden() {
  docViewerVisible.value = false
  if (resolvedLink.value?.startsWith('blob:')) {
    URL.revokeObjectURL(resolvedLink.value)
  }
  resolvedLink.value = null
  resolvedUpload.value = null
  resolvedDownloadUrl.value = null
}

defineExpose({
  openDoc,
  resolving,
})
</script>
<template>
  <template v-if="$slots.trigger">
    <slot
      name="trigger"
      :trigger="openDoc"
      :resolving="resolving"
    ></slot>
  </template>
  <a
    v-else-if="fileOk && isLink"
    href="#"
    @click.prevent="openDoc"
  >
    {{ upload.filename }}
  </a>
  <Docviewer
    v-if="($slots.trigger || fileOk) && docViewerVisible"
    :upload="activeUpload"
    :link="activeLink"
    :download-url="activeDownloadUrl"
    :versioning="versioning"
    :use-history="useHistory"
    :resolve-version="resolveVersion"
    @hidden="onHidden"
  >
    <template
      v-if="$slots.toolbar"
      #toolbar="slotProps"
    >
      <slot
        name="toolbar"
        v-bind="slotProps"
      />
    </template>
  </Docviewer>
</template>
